import { supabase } from './supabase';
import { ACCOUNTS } from './accounts';

// The dedicated supplier-payable control accounts (2000/2020/2030/2040/2070). Every line on
// these counts towards Accounts Payable, tagged or not — an untagged line is still money
// owed and has to show (as "Unallocated") or AP won't reconcile to the balance sheet.
export const PAYABLE_ACCOUNTS = [ACCOUNTS.AP_SUPPLIERS, ACCOUNTS.VISA_PAYABLE, ACCOUNTS.AIRLINE_PAYABLE, ACCOUNTS.HOTEL_LAND_PAYABLE, ACCOUNTS.COMMISSION_PAYABLE];

const SELECT = 'account_id, base_debit, base_credit, party_id, party_type, journal_entries!inner(entry_date, status, is_reversed, reversal_of)';

const fetchAll = async (build) => {
  const PAGE = 1000;
  let all = [], from = 0;
  while (true) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error || !data?.length) break;
    // A reversed entry and its reversal net to zero and represent no real activity —
    // exclude both, or a corrected mistake inflates gross cost/paid totals.
    all = all.concat(data.filter(l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of));
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
};

// Single source of truth for "what we owe suppliers" — used by both the Accounts Payable
// page and the Dashboard widget so the two can never disagree again. It is:
//   1. every POSTED line on the payable control accounts above, plus
//   2. supplier/agent-tagged lines on any OTHER liability account (e.g. 2075 Rajabdeen Haji
//      Float, a supplier-tagged line on 2060 Other Payables).
// Untagged lines on other liabilities (director/staff loans, deposit suspense, credit card,
// tax) are not supplier payables and are excluded.
export async function fetchSupplierPayableLines({ branchId } = {}) {
  const { data: liab } = await supabase.from('chart_of_accounts')
    .select('id, account_types!inner(category)').eq('is_active', true).eq('account_types.category', 'LIABILITY');
  const otherLiab = (liab || []).map(a => a.id).filter(id => !PAYABLE_ACCOUNTS.includes(id));

  const base = (accounts) => {
    let q = supabase.from('journal_lines').select(SELECT).in('account_id', accounts).eq('journal_entries.status', 'POSTED');
    if (branchId) q = q.eq('branch_id', branchId);
    return q;
  };

  const [control, tagged] = await Promise.all([
    fetchAll(() => base(PAYABLE_ACCOUNTS)),
    otherLiab.length ? fetchAll(() => base(otherLiab).in('party_type', ['supplier', 'agent'])) : [],
  ]);
  return control.concat(tagged);
}
